import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';

/**
 * Guard de permisos RBAC.
 *
 * Uso en `app.routes.ts`:
 * ```ts
 * {
 *   path: 'documentacion/plantillas',
 *   canActivate: [authGuard, permisoGuard],
 *   data: { permisos: [{ modulo: 'LEGAL', accion: 'VER' }] },
 *   ...
 * }
 * ```
 *
 * - Si el usuario **no está autenticado**: el `authGuard` previo ya redirige a `/login`.
 * - Si está autenticado pero **no tiene el permiso**: redirige a `/app` con `?acceso=denegado`.
 * - Si `data.permisos` está vacío o no existe: permite el acceso (igual que antes).
 */
export const permisoGuard: CanActivateFn = (route) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const permisos = route.data?.['permisos'] as
    | Array<{ modulo: string; accion: string }>
    | undefined;

  // Sin restricciones declaradas → acceso libre para autenticados
  if (!permisos?.length) return true;

  // Basta con tener AL MENOS UNO de los permisos listados (OR)
  const tieneAlguno = permisos.some((p) => auth.tienePermiso(p.modulo, p.accion));

  if (tieneAlguno) return true;

  return router.createUrlTree(['/app'], { queryParams: { acceso: 'denegado' } });
};
